import { FormControlOptions } from '@miq/catalog';

/**
 * This file depicts the shape of a "Property" form. it includes and configures inputs in controlConfigs
 * attribute that are not part of the catalog but that are essential for a form to have as hidden field
 * or values, usually id controls or hidden/missing columns
 *
 * Since we rely entirely on catalog to "generate" form and control options on each level, few attributes
 * are not provided in the catalog but needs to be present as explained above. Therefore this file works
 * as a skeleton form control options for those essential attributes that are not part of the catalog
 */
export const PropertyFormConfig: { [key: string]: FormControlOptions<any> } = Object.freeze({
  usages: {
    name: 'usages',
    type: 'array',
    controlsConfig: { id: [null], operating_expense_year: [null] },
    children: {
      company_contact_role_addresses: {
        name: 'company_contact_role_addresses',
        type: 'array',
        controlsConfig: {
          id: [''],
          _mode: [null],
          _contact_id: [null], // ensure presence
          display_order: [null], // ensure presence
          prev_display_order: [null], // ensure presence
          company_id: [null], // ensure presence
          contact_id: [null], // ensure presence
          role_desc: [null], // ensure presence
          location_id: [null], // ensure presence - flat mirror of location.location_id (MIQ-36890)
          ownership_percent: [null], // ensure presence
          ownership_type_desc: [null],
          children: [null],
          property_usage_id: [null],
          ref_property_usage_type_desc: [null],
        },
        children: {
          company_industry_classification: {
            name: 'company_industry_classification',
            type: 'array',
            controlsConfig: { id: [null], ref_naics_classification_desc: [null], primary_naics_code_f: [null] },
          },
          location: {
            name: 'location',
            type: 'group',
            controlsConfig: { location_id: [null] },
          },
          colloquial: {
            name: 'colloquial',
            type: 'array',
            controlsConfig: {
              id: [null],
              _mode: [null],
              country_code_desc: [null],
              language_desc: [null],
            },
          },
        },
      },
      property_parking: {
        name: 'property_parking',
        type: 'array',
        controlsConfig: { id: [null], parking_type_desc: [null], parking_sub_type_desc: [null], _mode: [null] },
      },
      lig_geography: {
        name: 'lig_geography',
        type: 'group',
      },
      usage_amenity: {
        name: 'usage_amenity',
        type: 'array',
        controlsConfig: {
          id: [null],
          _mode: [null],
          property_amenity_type_desc: [null],
          amenity_type_desc: [null],
        },
      },
      property_sign: {
        name: 'property_sign',
        type: 'array',
        controlsConfig: {
          id: [null],
          _mode: [null],
          sign_location_type_desc: [null],
        },
        children: {
          colloquial: {
            name: 'colloquial',
            type: 'array',
            controlsConfig: {
              id: [null],
              _mode: [null],
              property_sign_comment: [null],
              country_code_desc: [null],
              language_desc: [null],
            },
          },
        },
      },
      colloquial: {
        name: 'colloquial',
        type: 'array',
        controlsConfig: {
          id: [null],
          _mode: [null],
          country_code_desc: [null],
          language_desc: [null],
        },
      },
    },
  },
  digital: {
    name: 'digital',
    type: 'array',
    controlsConfig: { id: [null], _created: [''], _deleted: [''] },
  },
  property_elevator: {
    name: 'property_elevator',
    type: 'array',
    controlsConfig: {
      id: [null],
      _mode: [null],
      elevator_type_desc: [null],
    },
  },
  property_amenity: {
    name: 'property_amenity',
    type: 'array',
    controlsConfig: {
      id: [null],
      _mode: [null],
      property_amenity_type_desc: [null],
      amenity_type_desc: [null],
      amenity_name: [null],
    },
    children: {
      colloquial: {
        name: 'colloquial',
        type: 'array',
        controlsConfig: {
          id: [null],
          _mode: [null],
          property_amenity_type_desc: [null],
          amenity_type_desc: [null],
          amenity_name: [null],
          country_code_desc: [null],
          language_desc: [null],
        },
      },
    },
  },
  property_development: {
    name: 'property_development',
    type: 'array',
    controlsConfig: {
      id: [null],
    },
    children: {
      stages: {
        name: 'stages',
        type: 'array',
        controlsConfig: {
          id: [null],
        },
        children: {
          stage_notes: {
            name: 'stage_notes',
            type: 'array',
            controlsConfig: {
              id: [null],
              property_development_stage_notes_type_desc: [null],
            },
          },
        },
      },
      notes: {
        name: 'notes',
        type: 'array',
        controlsConfig: {
          id: [null],
          notes: [null],
          property_development_notes_type_desc: [null],
        },
      },
      company_contact_role_addresses: {
        name: 'company_contact_role_addresses',
        type: 'array',
        controlsConfig: {
          id: [null],
          _mode: [null],
          company_id: [null],
          contact_id: [null],
          role_desc: [null],
          children: [null],
        },
        children: {
          location: {
            name: 'location',
            type: 'group',
            controlsConfig: {
              location_id: [null],
            },
          },
        },
      },
    },
  },
  colloquial: {
    name: 'colloquial',
    type: 'array',
    controlsConfig: {
      country_code_desc: [null],
      language_desc: [null],
      id: [null],
      _mode: [null],
    },
  },

  property_landlord_provision: {
    name: 'property_landlord_provision',
    type: 'array',
    controlsConfig: {
      landlord_provision_type_desc: [null],
      _mode: [null],
      id: [null],
    },
    children: {
      colloquial: {
        name: 'colloquial',
        type: 'array',
        controlsConfig: {
          landlord_provision_type_desc: [null],
          country_code_desc: [null],
          language_desc: [null],
          id: [null],
          _mode: [null],
        },
      },
    },
  },
  property_note: {
    name: 'property_note',
    type: 'array',
    controlsConfig: {
      id: [null],
      property_notes_type_desc: [null],
      notes: [null],
      _mode: [null],
    },
    children: {
      colloquial: {
        name: 'colloquial',
        type: 'array',
        controlsConfig: {
          id: [null],
          _mode: [null],
          property_notes_type_desc: [null],
          country_code_desc: [null],
          language_desc: [null],
        },
      },
    },
  },
  asset: {
    name: 'asset',
    type: 'array',
    controlsConfig: { id: [null], asset_sub_type_desc: [null], _mode: [null] },
    children: {
      colloquial: {
        name: 'colloquial',
        type: 'array',
        controlsConfig: {
          asset_sub_type_desc: [null],
          country_code_desc: [null],
          language_desc: [null],
          id: [null],
          _mode: [null],
        },
      },
    },
  },
  postal_address: {
    name: 'postal_address',
    type: 'group',
    children: {
      colloquial: {
        name: 'colloquial',
        type: 'array',
        controlsConfig: {
          country_code_desc: [null],
          language_desc: [null],
          id: [null],
          _mode: [null],
        },
      },
    },
    controlsConfig: { id: [null] },
  },
  property_apn_number: {
    name: 'property_apn_number',
    type: 'array',
    controlsConfig: { property_apn_number: [null], id: [null], _mode: [null] },
  },
  property_external_refs: {
    name: 'property_external_refs',
    type: 'array',
    controlsConfig: {
      id: [null],
      _mode: [null],
      property_external_refs: [''],
      property_external_ref_type_desc: [''],
      property_external_ref_subtype_desc: [''],
      data_publication: [''],
      property_external_ref_id: [null],
    },
  },
  elevator_bank: {
    name: 'elevator_bank',
    type: 'array',
    controlsConfig: { id: [null] },
    children: {
      colloquial: {
        name: 'colloquial',
        type: 'array',
        controlsConfig: {
          country_code_desc: [null],
          language_desc: [null],
          id: [null],
          _mode: [null],
        },
      },
    },
  },
  property_air_condition: {
    name: 'property_air_condition',
    type: 'array',
    controlsConfig: {
      id: [null],
      _mode: [null],
      property_air_condition_desc: [null],
      air_condition_type_desc: [null],
      week_day_type_desc: [null],
    },
  },
  property_measurement: {
    name: 'property_measurement',
    type: 'array',
    controlsConfig: {
      id: [null],
    },
  },
  property_parking: {
    name: 'property_parking',
    type: 'array',
    controlsConfig: {
      id: [null],
    },
  },
  property_document: {
    name: 'property_document',
    type: 'array',
    controlsConfig: {
      id: [null],
    },
  },
  property_frontage: {
    name: 'property_frontage',
    type: 'array',
    controlsConfig: {
      id: [null],
    },
  },
  property_external_ratings: {
    name: 'property_external_ratings',
    type: 'array',
    controlsConfig: { id: [null], _mode: [null] },
  },
  alternate_postal_addresses: {
    name: 'alternate_postal_addresses',
    type: 'array',
    controlsConfig: {
      id: [null],
      ref_address_usage_type_desc: [null],
      _mode: [null],
      _isEditing: [null],
    },
  },
  miqProperties: {
    name: 'miqProperties',
    type: 'group',
    children: {
      calculated_fields: {
        name: 'calculated_fields',
        type: 'group',
        controlsConfig: {
          percentage_studios: [null],
        },
      },
    },
  },
  property_debt: {
    type: 'array',
    name: 'property_debt',
    controlsConfig: {
      id: [null],
      _mode: [null],
    },
  },
  property_operating_expense_history: {
    name: 'property_operating_expense_history',
    type: 'array',
    controlsConfig: {
      id: [null],
      _mode: [null],
    },
  },
  property_tax_assessment: {
    name: 'property_tax_assessment',
    type: 'array',
    controlsConfig: {
      id: [null],
      _mode: [null],
    },
  },
});
